/* Native dialog supplies focus containment, Escape dismissal and focus restoration. */
if (!customElements.get('haro-supplement-facts')) {
  customElements.define('haro-supplement-facts', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      // A definition loaded by another section can upgrade this element before
      // the parser has added its dialog and controls.
      if (document.readyState === 'loading') {
        if (!this.waitingForDocument) {
          this.waitingForDocument = true;
          document.addEventListener('DOMContentLoaded', () => {
            this.waitingForDocument = false;
            if (this.isConnected) this.connectedCallback();
          }, { once: true });
        }
        return;
      }
      const dialog = this.querySelector('dialog');
      const trigger = this.querySelector('[data-facts-open]');
      if (!dialog || !trigger || typeof dialog.showModal !== 'function') return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const summary = this.querySelector('[data-facts-summary]');
      const labelView = this.querySelector('[data-label-view]');
      const labelOpen = this.querySelector('[data-label-open]');
      const labelBack = this.querySelector('[data-label-back]');
      const labelZoomOut = this.querySelector('[data-label-zoom-out]');
      const labelZoomIn = this.querySelector('[data-label-zoom-in]');
      const labelZoomReset = this.querySelector('[data-label-zoom-reset]');
      const labelZoomLevel = this.querySelector('[data-label-zoom-level]');
      const labelImage = this.querySelector('[data-label-image]');
      const title = dialog.querySelector('h3');
      const zoomSteps = [100, 150, 200, 250, 300];
      let zoomIndex = 0;
      let summaryScroll = 0;
      let dragPointerId;
      let dragStartX = 0;
      let dragStartY = 0;
      let dragStartScrollLeft = 0;
      let dragStartScrollTop = 0;
      const endDrag = (event) => {
        if (event && dragPointerId !== event.pointerId) return;
        if (dragPointerId !== undefined && labelImage.hasPointerCapture?.(dragPointerId)) {
          labelImage.releasePointerCapture(dragPointerId);
        }
        dragPointerId = undefined;
        labelImage.classList.remove('is-dragging');
      };
      const updateZoomControls = () => {
        const zoom = zoomSteps[zoomIndex];
        labelZoomLevel.value = `${zoom}%`;
        labelZoomLevel.textContent = `${zoom}%`;
        labelZoomOut.disabled = zoomIndex === 0;
        labelZoomIn.disabled = zoomIndex === zoomSteps.length - 1;
        labelZoomReset.disabled = zoomIndex === 0 && labelImage.scrollLeft === 0 && labelImage.scrollTop === 0;
      };
      const setZoom = (nextIndex, resetPosition = false) => {
        endDrag();
        const oldZoom = zoomSteps[zoomIndex];
        const oldCenterX = labelImage.scrollLeft + labelImage.clientWidth / 2;
        const oldCenterY = labelImage.scrollTop + labelImage.clientHeight / 2;
        zoomIndex = Math.max(0, Math.min(nextIndex, zoomSteps.length - 1));
        const zoom = zoomSteps[zoomIndex];
        labelImage.style.setProperty('--haro-label-zoom', `${zoom}%`);
        labelImage.classList.toggle('is-enlarged', zoom > 100);
        updateZoomControls();
        window.requestAnimationFrame(() => {
          if (resetPosition || zoom === 100) {
            labelImage.scrollTo(0, 0);
          } else {
            const scale = zoom / oldZoom;
            labelImage.scrollTo(oldCenterX * scale - labelImage.clientWidth / 2, oldCenterY * scale - labelImage.clientHeight / 2);
          }
          updateZoomControls();
        });
      };
      const resetZoom = () => setZoom(0, true);
      labelOpen.addEventListener('click', () => {
        summaryScroll = dialog.scrollTop;
        summary.hidden = true;
        labelView.hidden = false;
        title.textContent = (window.haroMessages?.original_label_2bc0a || "Original label");
        dialog.scrollTop = 0;
        labelBack.focus({ preventScroll: true });
      }, options);
      labelBack.addEventListener('click', () => {
        labelView.hidden = true;
        summary.hidden = false;
        title.textContent = (window.haroMessages?.supplement_facts_12726 || "Supplement Facts");
        resetZoom();
        labelOpen.focus({ preventScroll: true });
        dialog.scrollTop = summaryScroll;
      }, options);
      labelZoomOut.addEventListener('click', () => setZoom(zoomIndex - 1), options);
      labelZoomIn.addEventListener('click', () => setZoom(zoomIndex + 1), options);
      labelZoomReset.addEventListener('click', resetZoom, options);
      labelImage.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || event.button !== 0 || !labelImage.classList.contains('is-enlarged')) return;
        event.preventDefault();
        dragPointerId = event.pointerId;
        dragStartX = event.clientX;
        dragStartY = event.clientY;
        dragStartScrollLeft = labelImage.scrollLeft;
        dragStartScrollTop = labelImage.scrollTop;
        labelImage.classList.add('is-dragging');
        labelImage.setPointerCapture(event.pointerId);
      }, options);
      labelImage.addEventListener('pointermove', (event) => {
        if (dragPointerId !== event.pointerId) return;
        event.preventDefault();
        labelImage.scrollLeft = dragStartScrollLeft - (event.clientX - dragStartX);
        labelImage.scrollTop = dragStartScrollTop - (event.clientY - dragStartY);
      }, options);
      labelImage.addEventListener('pointerup', endDrag, options);
      labelImage.addEventListener('pointercancel', endDrag, options);
      labelImage.addEventListener('scroll', updateZoomControls, options);
      labelImage.addEventListener('lostpointercapture', () => {
        dragPointerId = undefined;
        labelImage.classList.remove('is-dragging');
      }, options);
      labelImage.addEventListener('dragstart', (event) => event.preventDefault(), options);
      dialog.addEventListener('close', () => {
        labelView.hidden = true;
        summary.hidden = false;
        title.textContent = (window.haroMessages?.supplement_facts_12726 || "Supplement Facts");
        resetZoom();
        dialog.scrollTop = 0;
      }, options);
      trigger.setAttribute('aria-haspopup', 'dialog');
      updateZoomControls();
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (!dialog.open) dialog.showModal();
      }, options);
      if (new URLSearchParams(window.location.search).get('open_supplement_facts') === '1' &&
          !document.documentElement.hasAttribute('data-haro-facts-query-opened')) {
        document.documentElement.setAttribute('data-haro-facts-query-opened', 'true');
        window.requestAnimationFrame(() => trigger.click());
      }
      dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const controls = [...dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')]
          .filter((element) => element.getClientRects().length > 0);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      }, options);
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right ||
            event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      }, options);
    }
    disconnectedCallback() {
      this.querySelector('dialog')?.close();
      this.controller?.abort();
      this.controller = null;
    }
  });
}

if (!window.haroSupplementFactsGlobalLinkBound) {
  window.haroSupplementFactsGlobalLinkBound = true;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-facts-global-open]');
    if (!link) return;
    const trigger = document.querySelector('haro-supplement-facts [data-facts-open]');
    if (!trigger) return;
    event.preventDefault();
    trigger.click();
  });
}
